import {translate} from "@prosay/i18n"
import {Draft} from "immer"
import React, {useCallback, useEffect} from "react"
import {ScrollView, StyleSheet, View} from "react-native"
import {Chip} from "react-native-paper"
import {useImmer} from "use-immer"
import {ConversationStatus} from "../graphql"

const styles = StyleSheet.create({
    chip: {marginHorizontal: 2},
    selectedChip: {},
    statusSelectorContainer: {
        backgroundColor: "rgb(0, 113, 212)",
        flexDirection: "row",
        padding: 5,
    },
    unselectedChip: {},
})

export interface StatusChip {
    icon?: string
    key: string
    name: string
    selected: boolean
    status: readonly ConversationStatus[]
}

export interface StatusSelectorChipProps {
    index: number
    item: Omit<StatusChip, "key">
    updateChips: (
        f: (
            draft: Draft<readonly StatusChip[]>,
        ) => void | readonly StatusChip[],
    ) => void
}
export const StatusSelectorChip: React.FC<StatusSelectorChipProps> = ({
    index,
    item: {icon, name, selected},
    updateChips,
}) => {
    const onPress = useCallback(
        () =>
            updateChips(draft => {
                draft[index].selected = !draft[index].selected
            }),
        [index, updateChips],
    )

    return (
        <Chip
            mode="outlined"
            style={[
                styles.chip,
                selected ? styles.selectedChip : styles.unselectedChip,
            ]}
            {...{icon, onPress, selected}}>
            {name}
        </Chip>
    )
}

const ClientChips = [
    {
        name: translate("conversation.status.unresolved"),
        selected: true,
        status: ["InformationGathering", "LookingForLawyer", "InProgress"],
    },
    {
        name: translate("conversation.status.resolved"),
        selected: true,
        status: ["Resolved"],
    },
] as const

const LawyerChips = [
    {
        name: translate("conversation.status.unresolved"),
        selected: true,
        status: ["InformationGathering", "LookingForLawyer", "InProgress"],
    },
    {
        name: translate("conversation.status.resolved"),
        selected: true,
        status: ["Resolved"],
    },
] as const

const makeStatusChipList = (list: typeof ClientChips | typeof LawyerChips) =>
    list.map(({...props}) => ({
        ...props,
        key: props.status.join(";"),
    }))

export interface StatusSelectorProps {
    onStatusUpdate: (status: readonly ConversationStatus[]) => void
    type: "Client" | "Lawyer"
}
export const StatusSelector: React.FC<StatusSelectorProps> = ({
    onStatusUpdate,
    type,
}) => {
    const [chips, updateChips] = useImmer<readonly StatusChip[]>(
        makeStatusChipList(type === "Lawyer" ? LawyerChips : ClientChips),
    )

    useEffect(
        () =>
            void onStatusUpdate(
                chips
                    .filter(({selected}) => selected)
                    .flatMap(({status}) => status),
            ),
        [chips, onStatusUpdate],
    )

    return (
        <View style={styles.statusSelectorContainer}>
            <ScrollView alwaysBounceHorizontal horizontal>
                {chips.map(({key, ...item}, index) => (
                    <StatusSelectorChip {...{index, item, key, updateChips}} />
                ))}
            </ScrollView>
        </View>
    )
}
